import { useEffect, useState } from "react";
import { API_BASE } from "../../config/api";
import { useAuth } from "../../contexts/AuthContext";
import { FaUser, FaNotesMedical, FaMinus, FaPlus } from "react-icons/fa";

export default function NewCaseForm({ editPatient = null }) {
  const [step, setStep] = useState(1);
  const [caseSection, setCaseSection] = useState("presentcomplaint");
  const [fontSize, setFontSize] = useState(14);
  const [errors, setErrors] = useState({});
  const { getAuthHeaders } = useAuth();

  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    email: "",
    age: "",
    gender: "",
    mobilenumber: "",
    registration_number: "",
    date: "",
    time: "",
    wt: "",
    bp: "",
    presentcomplaint: "",
    pasthistory: "",
    head: "",
    eyes: "",
    ears: "",
    mouth: "",
    nose: "",
    throat: "",
    chest: "",
    abdomen: "",
    appetite: "",
    thrist: "",
    urine: "",
    stool: "",
    sleep: "",
    mind: "",
    ext: "",
    skin: "",
    sweat: "",
    female: "",
    thermal: "",
    investigation: "",
    face: "",
    physiology: "",
    familyhistory: "",
    male: "",
    treatment: "",
  });

  useEffect(() => {
    if (editPatient) {
      const formattedDate = editPatient.date
        ? new Date(editPatient.date).toISOString().split("T")[0]
        : "";
      const formattedTime = editPatient.time
        ? new Date(`1970-01-01T${editPatient.time}`).toTimeString().slice(0, 5)
        : "";

      setForm((prev) => {
        const updatedForm = { ...prev };
        Object.keys(prev).forEach((key) => {
          if (key === "date") updatedForm[key] = formattedDate;
          else if (key === "time") updatedForm[key] = formattedTime;
          else updatedForm[key] = editPatient[key] ?? "";
        });
        updatedForm.id = editPatient.id;
        return updatedForm;
      });

      if (editPatient.firstname && editPatient.age && editPatient.gender) {
        setStep(2);
      }
    } else {
      const now = new Date();
      const date = now.toISOString().split("T")[0];
      const time = now.toTimeString().slice(0, 5);
      setForm((prev) => ({ ...prev, date, time }));

      fetch(`${API_BASE}/patients/next-reg`, {
        headers: getAuthHeaders()
      })
        .then((res) => res.json())
        .then((data) => {
          setForm((prev) => ({
            ...prev,
            registration_number: data?.next || "",
          }));
        })
        .catch((err) => console.error("Reg No Fetch Error", err));
    }
  }, [editPatient, getAuthHeaders]);

  const validateField = (name, value) => {
    let error = "";

    if (name === "firstname" && value.trim() === "") {
      error = "First name is required";
    } else if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) error = "Enter a valid email";
    } else if (name === "mobilenumber") {
      if (value && !/^\d{10}$/.test(value)) error = "Enter 10-digit mobile number";
    } else if (name === "age") {
      const ageNum = Number(value);
      if (!value || ageNum < 1 || ageNum > 150) error = "Age must be between 1 and 150";
    } else if (name === "gender" && value === "") {
      error = "Gender is required";
    } else if (name === "wt" && value && Number(value) <= 0) {
      error = "Weight must be greater than 0";
    } else if (name === "bp" && value && !/^\d{2,3}\/\d{2,3}$/.test(value)) {
      error = "BP must be in format xx/xx";
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const autoFormatBP = (value) => {
    if (/^\d{4,6}$/.test(value)) {
      const half = Math.floor(value.length / 2);
      return `${value.slice(0, half)}/${value.slice(half)}`;
    }
    return value;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === "bp") value = autoFormatBP(value);

    setForm((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);

    if (!editPatient) {
      localStorage.setItem("new-case-draft", JSON.stringify({ ...form, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasErrors = Object.values(errors).some((err) => err);
    if (hasErrors) {
      alert("Please fix validation errors before submitting.");
      return;
    }
    
    try {
      const url = editPatient
        ? `${API_BASE}/patients/update/${form.id}`
        : `${API_BASE}/patients/new`;
      const method = editPatient ? "PUT" : "POST";
      const action = editPatient ? "update" : "add";
      const confirmPrompt = window.confirm(`Do you want to ${action} the case?`);
      console.log("Form keys count:", Object.keys(form).length);
      console.log("Form keys:", Object.keys(form));
      console.log("Form values:", Object.values(form));

      const res = await fetch(url, {
        method,
        headers: { 
          ...getAuthHeaders(),
          "Content-Type": "application/json" 
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Failed to save");

      if (confirmPrompt) {
        window.location.reload();
      }

      setForm({});
      setStep(1);
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const requiredFieldsFilled = () => {
    return (
      form.firstname &&
      form.age &&
      Number(form.age) > 0 &&
      Number(form.age) <= 150 &&
      form.gender
    );
  };

  const inputClass =
    "w-full px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 transition";

  const getInputClass = (field) =>
    `${inputClass} ${
      errors[field]
        ? "border-red-500 ring-red-400"
        : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
    }`;

  const caseTabs = [
    ["presentcomplaint", "Present Complaint"],
    ["pasthistory", "Past History"],
    ["head", "Head"],
    ["eyes", "Eyes"],
    ["ears", "Ears"],
    ["mouth", "Mouth"],
    ["nose", "Nose"],
    ["throat", "Throat"],
    ["chest", "Chest"],
    ["abdomen", "Abdomen"],
    ["appetite", "Appetite"],
    ["thrist", "Thirst"],
    ["urine", "Urine"],
    ["stool", "Stool"],
    ["sleep", "Sleep"],
    ["mind", "Mind"],
    ["ext", "Extremities"],
    ["skin", "Skin"],
    ["sweat", "Sweat"],
    ["female", "Female"],
    ["thermal", "Thermal"],
    ["investigation", "Investigation"],
    ["face", "Face"],
    ["physiology", "Physiology"],
    ["familyhistory", "Family History"],
    ["male", "Male"],
    ["treatment", "Treatment"],
  ];

  return (
    <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-xl max-w-6xl mx-auto mt-6">
      <div className="flex mb-6 text-sm font-semibold">
        <div className={`flex-1 text-center py-2 rounded-l-full ${step === 1 ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"}`}>
          <FaUser className="inline mr-2" />
          Patient Info
        </div>
        <div className={`flex-1 text-center py-2 rounded-r-full ${step === 2 ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-700"}`}>
          <FaNotesMedical className="inline mr-2" />
          Clinical Case
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {[
              ["firstname", "First Name *", "text"],
              ["lastname", "Last Name", "text"],
              ["email", "Email", "email"],
              ["mobilenumber", "Mobile Number", "tel"],
              ["age", "Age *", "number"],
              ["gender", "Gender *", "select"],
              ["wt", "Weight (kg)", "number"],
              ["bp", "Blood Pressure", "text"],
              ["date", "Date", "date"],
              ["time", "Time", "time"],
            ].map(([field, label, type]) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                {type === "select" ? (
                  <select
                    name={field}
                    value={form[field]}
                    onChange={handleChange}
                    className={getInputClass(field)}
                    required={label.includes("*")}
                  >
                    <option value="">Select</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Other">Other</option>
                  </select>
                ) : (
                  <input
                    type={type}
                    name={field}
                    value={form[field]}
                    onChange={handleChange}
                    className={getInputClass(field)}
                    required={label.includes("*")}
                    min={field === "age" || field === "wt" ? 1 : undefined}
                  />
                )}
                {errors[field] && (
                  <p className="text-red-500 text-xs mt-1">{errors[field]}</p>
                )}
              </div>
            ))}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Registration Number</label>
              <input type="text" name="registration_number" value={form.registration_number} readOnly className={`${inputClass} bg-gray-100`} />
            </div>

            <div className="md:col-span-2 text-right pt-2">
              <button
                type="button"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium text-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() =>
                  requiredFieldsFilled() ? setStep(2) : alert("Please fill all required fields")
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <>

            <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200">
              {caseTabs.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setCaseSection(key)}
                  className={`px-3 py-1 text-sm border rounded-md whitespace-nowrap transition ${
                    caseSection === key
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {caseTabs.find(([k]) => k === caseSection)?.[1]}
              </label>
              <textarea
                rows="10"
                style={{ fontSize: `${fontSize}px` }}
                name={caseSection}
                value={form[caseSection]}
                onChange={handleChange}
                className={`${inputClass} resize-none`}
              />
              <div className="absolute right-2 top-0 flex gap-1">
                <button
                  type="button"
                  onClick={() => setFontSize((f) => Math.max(10, f - 1))}
                  className="p-1 rounded-md text-blue-600 hover:bg-blue-100"
                >
                  <FaMinus />
                </button>
                <button
                  type="button"
                  onClick={() => setFontSize((f) => f + 1)}
                  className="p-1 rounded-md text-blue-600 hover:bg-blue-100"
                >
                  <FaPlus />
                </button>
              </div>
            </div>
            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-blue-600 text-sm font-medium hover:underline"
              >
                ← Back to Patient Info
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium text-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {editPatient ? "Update Case" : "Save Case"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
